import { defineStore, acceptHMRUpdate } from 'pinia';
import { SURVEY_DISPLAY_ACRONYMS, SURVEY_TYPES } from '@/surveys/constants/surveys.constants';
import * as api from '@/vuex/modules/surveys/api/surveys.api.js';
import { runAggregationOnce } from '@utils/aggregation';
import { hasSegmentFlag } from '@/data/flags/local-flags';
import SurveyItemsAllAggregation from '@/surveys/aggregations/survey-items/survey-items-all.aggregation';

interface Survey {
    id: string;
    name?: string;
    items?: unknown[];
    [key: string]: unknown;
}

interface SurveysState {
    surveys: Record<string, Survey>;
    surveyItems: unknown[];
}

// Module-level promise deduplication keyed by operation
const promises: Record<string, Promise<unknown> | null> = {};

export function __resetPromisesForUnitTests () {
    for (const key of Object.keys(promises)) {
        promises[key] = null;
    }
}

export function getInitialState (): SurveysState {
    return {
        surveys: {},
        surveyItems: [],
    };
}

export const useSurveysStore = defineStore('surveys', {
    state: getInitialState,
    getters: {
        list: (state) => {
            if (hasSegmentFlag('npsOnSentiment')) {
                return state.surveys;
            }

            return Object.fromEntries(
                Object.entries(state.surveys).filter(([, survey]) => survey.surveyType !== SURVEY_TYPES.NET_PROMOTER_SCORE),
            );
        },
        listItems: (state) => state.surveyItems,
        surveyById: (state) => (id: string): Survey | undefined => state.surveys[id],
    },
    actions: {
        async loadAll () {
            if (promises.loadAll) {
                return promises.loadAll;
            }

            const loadAllPromise = api
                .fetchSurveys()
                .then((surveys: Record<string, Survey>) => {
                    this.surveys = surveys;

                    return surveys;
                })
                .finally(() => {
                    promises.loadAll = null;
                });

            promises.loadAll = loadAllPromise;

            return loadAllPromise;
        },
        async loadOne ({ id }: { id: string }) {
            const key = `loadOne-${id}`;
            if (promises[key]) {
                return promises[key];
            }

            const loadOnePromise = api
                .fetchSurvey(id)
                .then((survey: Survey) => {
                    this.surveys[id] = survey;

                    return survey;
                })
                .finally(() => {
                    promises[key] = null;
                });

            promises[key] = loadOnePromise;

            return loadOnePromise;
        },
        async loadAllItems () {
            if (promises.loadAllItems) {
                return promises.loadAllItems;
            }

            const loadItemsPromise = runAggregationOnce(SurveyItemsAllAggregation, {})
                .then((surveyItems: unknown) => {
                    this.surveyItems = surveyItems as unknown[];
                })
                .finally(() => {
                    promises.loadAllItems = null;
                });
            promises.loadAllItems = loadItemsPromise;

            return loadItemsPromise;
        },
        async createFromTemplate ({ type, productName, appId, productAreaIds, deliveryMethods, guideLayout, hideThankYou, snoozeEnabled }: {
            type: string;
            productName: string;
            appId: string;
            productAreaIds: string[];
            deliveryMethods: unknown;
            guideLayout?: string;
            hideThankYou?: boolean;
            snoozeEnabled?: boolean;
        }) {
            const payload = {
                surveyType: type,
                surveyData: { productName },
                name: `${SURVEY_DISPLAY_ACRONYMS[type as keyof typeof SURVEY_DISPLAY_ACRONYMS]} Survey for ${productName}`,
                appIds: [appId],
                productAreaIds,
                delivery: deliveryMethods,
                hideThankYou,
                singleStep: guideLayout && guideLayout !== 'MULTI_STEP',
                snoozeEnabled,
            };

            const res = await api.createSurveyFromTemplate({ payload });
            const survey = { ...res.survey, items: res.surveyItems };
            this.surveys[survey.id] = survey;

            return survey;
        },
        async updateStatus ({ id, status }: { id: string; status: string }) {
            const survey = await api.updateSurveyStatus({ id, status });
            this.surveys[survey.id] = survey;

            return survey;
        },
        async updateSurveyName ({ id, name }: { id: string; name: string }) {
            const rollbackSurveyState = this.surveyById(id);
            this.surveys[id] = { ...this.surveys[id], name };

            try {
                const updatedSurvey = await api.updateSurveyDetails({ id, payload: { name } });
                this.surveys[id] = updatedSurvey;

                return updatedSurvey;
            } catch (error) {
                if (rollbackSurveyState) {
                    this.surveys[id] = rollbackSurveyState;
                }
                throw error;
            }
        },
        async updateSurveyProductAreaId ({ id, productAreaIds }: { id: string; productAreaIds: string[] }) {
            const survey = await api.updateSurveyDetails({ id, payload: { productAreaIds } });
            this.surveys[survey.id] = survey;

            return survey;
        },
        async updateSurveySnoozeEnabled ({ id, snoozeEnabled }: { id: string; snoozeEnabled: boolean }) {
            const survey = await api.updateSurveyDetails({ id, payload: { snoozeEnabled } });
            this.surveys[survey.id] = { ...this.surveys[survey.id], ...survey };

            return survey;
        },
        async updateDelivery ({ id, audienceConfig, deliveryId }: { id: string; audienceConfig: unknown; deliveryId: string }) {
            const survey = await api.updateSurveyDelivery({ id, audienceConfig, deliveryId });
            this.surveys[survey.id] = survey;

            return survey;
        },
        async updateDeliveryItems ({ id, deliveryId, deliveryConfig }: { id: string; deliveryId: string; deliveryConfig: unknown }) {
            const survey = await api.updateSurveyDeliveryItems({ id, deliveryId, deliveryConfig });
            this.surveys[survey.id] = survey;

            return survey;
        },
        async upsertEmailDelivery ({ id, payload }: { id: string; payload: unknown }) {
            const survey = await api.upsertSurveyEmailDelivery({ id, payload });
            this.surveys[survey.id] = survey;

            return survey;
        },
        async updateSurveyRecurrence (params: { id: string; recurrence: unknown }) {
            const survey = await api.updateSurveyRecurrence(params);
            this.surveys[survey.id] = survey;

            return survey;
        },
        async delete ({ id }: { id: string }) {
            await api.deleteSurvey(id);
            delete this.surveys[id];
        },
        async cloneSurvey ({ surveyId, targetAppId }: { surveyId: string; targetAppId: string }) {
            const res = await api.cloneSurvey({ surveyId, targetAppId });
            const survey = { ...res.survey, items: res.surveyItems };
            this.surveys[survey.id] = survey;

            return survey;
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useSurveysStore, module.hot));
}
